import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ColumnDef } from '@tanstack/react-table';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { ArrowUpDown, Check, GripVertical, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import apiService from '../services/api.service';
import { cn } from '../lib/utils';
import { Category, CategoryGroup } from '../types';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { StatusDot } from '../components/ui/status-dot';
import { PageHeader, Toolbar } from '../components/ui/page-header';
import { UnderlineTabs } from '../components/ui/underline-tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { DataTable } from '../components/data-table/DataTable';
import { BulkAction, BulkActionBar } from '../components/data-table/BulkActionBar';
import { FilterChip } from '../components/data-table/FilterChip';
import { SearchField } from '../components/data-table/SearchField';
import { CategoryFormValues, CategoryPatch, CategorySheet } from '../components/categories/CategorySheet';
import { CategoryGroupFormValues, CategoryGroupPatch, CategoryGroupSheet } from '../components/categories/CategoryGroupSheet';
import { categoriesQueryKey, useCategories, useTeams } from '../hooks/useTeams';
import { categoryGroupsQueryKey, useCategoryGroups } from '../hooks/useCategoryGroups';

// Catégories (refonte 2026-09-27) : une page, deux onglets — Catégories et
// Groupes. Un groupe n'est qu'une combinaison de catégories pour une heat ;
// deux entrées de menu pour la même notion se lisaient comme deux notions.
// Grammaire de la page Équipes : tableau triable, recherche, panneau latéral,
// sélection multiple. La colonne « Inscrites » compte les équipes de la
// catégorie, ce qu'aucun écran ne disait avant.
//
// « Réorganiser » reste un MODE à part : glisser une ligne n'a de sens que
// pour l'ordre, et l'ordre ne se devine pas depuis un tableau qu'on peut trier
// par n'importe quelle colonne. Il donne l'ordre de génération des heats.
//
// Sauvegarde automatique (règle du 22/09) : l'ordre ne rend rien public de
// plus (la page publique liste déjà les catégories) et ne prévient personne,
// donc chaque dépôt s'écrit tout de suite, avec « Annuler » sur le message ;
// le mode ne garde qu'un bouton « Terminé ». Les panneaux écrivent chaque
// champ au blur, seule la création garde son bouton.

type Tab = 'categories' | 'groups';

const formatError = (err: any, fallback: string): string => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
  return fallback;
};

const Categories: React.FC = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedEventId, selectedEvent } = useEvent();
  const tab: Tab = searchParams.get('tab') === 'groups' ? 'groups' : 'categories';
  const setTab = (next: string) => { searchParams.set('tab', next); setSearchParams(searchParams, { replace: true }); };

  const categoriesQuery = useCategories(selectedEventId);
  const groupsQuery = useCategoryGroups(selectedEventId);
  const teamsQuery = useTeams(selectedEventId);
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);
  const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);
  const teamCount = useCallback((catId: string) => (teamsQuery.data ?? []).filter((tm) => tm.category_id === catId).length, [teamsQuery.data]);
  const categoryName = useCallback((id: string) => categories.find((c) => c.id === id)?.name ?? id, [categories]);
  const refreshCategories = () => { qc.invalidateQueries({ queryKey: categoriesQueryKey(selectedEventId) }); qc.invalidateQueries({ queryKey: categoryGroupsQueryKey(selectedEventId) }); };
  const refreshGroups = () => qc.invalidateQueries({ queryKey: categoryGroupsQueryKey(selectedEventId) });

  const genderLabel = useCallback((g?: string) => ({ M: t('categories.genderMale'), F: t('categories.genderFemale'), MIXED: t('categories.genderMixed') } as Record<string, string>)[g ?? ''] ?? t('categories.genderNotSpecified'), [t]);
  const ageLabel = useCallback((c: Category) => (c.min_age || c.max_age ? `${c.min_age ?? 0} – ${c.max_age ?? '∞'} ${t('common.years')}` : t('common.allAges')), [t]);

  // ----- Filtres -----
  const [query, setQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<string[]>([]);
  useEffect(() => { setQuery(''); }, [tab]);

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    return categories.filter((c) => {
      if (genderFilter.length && !genderFilter.includes(c.gender ?? 'none')) return false;
      return !q || [c.name, c.display_name, c.description, genderLabel(c.gender)].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [categories, query, genderFilter, genderLabel]);

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups.filter((g) => {
      if (activeFilter.length && !activeFilter.includes(g.is_active ? 'active' : 'inactive')) return false;
      return !q || [g.name, g.description, ...g.category_ids.map(categoryName)].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [groups, query, activeFilter, categoryName]);

  // ----- Sélection -----
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const selectedIds = Object.keys(selection).filter((id) => selection[id]);
  useEffect(() => { setSelection({}); }, [selectedEventId, tab]);

  // ----- Panneaux -----
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [sheetCreate, setSheetCreate] = useState(false);
  const [sheetDirty, setSheetDirty] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const sheetCategory = tab === 'categories' ? categories.find((c) => c.id === sheetId) ?? null : null;
  const sheetGroup = tab === 'groups' ? groups.find((g) => g.id === sheetId) ?? null : null;
  const sheetOpen = sheetCreate || !!sheetCategory || !!sheetGroup;
  const visibleIds = tab === 'categories' ? filteredCategories.map((c) => c.id) : filteredGroups.map((g) => g.id);
  const indexInView = visibleIds.indexOf(sheetId ?? '');
  const openRow = (id: string) => {
    if (sheetOpen && sheetDirty && id !== sheetId) { setPendingSwitch(id); return; }
    setSheetCreate(false);
    setSheetId(id);
  };
  const closeSheet = () => { setSheetId(null); setSheetCreate(false); setServerError(''); };
  useEffect(() => { closeSheet(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, selectedEventId]);

  // `?create=true` (vue d'ensemble, palette ⌘K) ouvre le panneau de création.
  useEffect(() => {
    if (searchParams.get('create') === 'true' && selectedEventId) {
      setSheetId(null); setSheetCreate(true);
      searchParams.delete('create'); setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('create'), selectedEventId]);

  const submitCategory = async (values: CategoryFormValues) => {
    if (!selectedEventId) return;
    setSaving(true); setServerError('');
    const name = values.name.trim();
    const payload = {
      name,
      display_name: values.display_name.trim() || null,
      description: values.description,
      gender: values.gender || null,
      min_age: values.min_age ? parseInt(values.min_age, 10) : null,
      max_age: values.max_age ? parseInt(values.max_age, 10) : null,
      event_id: selectedEventId,
    };
    try {
      await apiService.createCategory(payload);
      toast.success(t('categories.sheet.created', { name }));
      setSheetDirty(false); closeSheet(); refreshCategories();
    } catch (err) {
      setServerError(formatError(err, t('errors.generic')));
    } finally { setSaving(false); }
  };

  // Édition : le panneau écrit champ par champ. Le serveur est partiel (clé
  // absente = préservée), on n'envoie que ce qui a bougé.
  const patchCategory = async (patch: CategoryPatch) => {
    if (!sheetCategory) return;
    await apiService.updateCategory(sheetCategory.id, patch);
    refreshCategories();
  };

  const submitGroup = async (values: CategoryGroupFormValues) => {
    if (!selectedEventId) return;
    setSaving(true); setServerError('');
    try {
      await apiService.post(`/events/${selectedEventId}/category-groups`, values);
      toast.success(t('categoryGroups.sheet.created', { name: values.name }));
      setSheetDirty(false); closeSheet(); refreshGroups();
    } catch (err) {
      setServerError(formatError(err, t('errors.generic')));
    } finally { setSaving(false); }
  };

  const patchGroup = async (patch: CategoryGroupPatch) => {
    if (!sheetGroup) return;
    await apiService.put(`/category-groups/${sheetGroup.id}`, patch);
    refreshGroups();
  };

  // ----- Suppression (confirmée : irréversible) -----
  const [toDelete, setToDelete] = useState<{ ids: string[]; names: string[] }>({ ids: [], names: [] });
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    setDeleting(true);
    const failed: string[] = [];
    for (let i = 0; i < toDelete.ids.length; i++) {
      try {
        if (tab === 'categories') await apiService.deleteCategory(toDelete.ids[i]);
        else await apiService.delete(`/category-groups/${toDelete.ids[i]}`);
      } catch { failed.push(toDelete.names[i]); }
    }
    setDeleting(false); setSelection({});
    if (toDelete.ids.includes(sheetId ?? '')) closeSheet();
    setToDelete({ ids: [], names: [] });
    if (tab === 'categories') refreshCategories(); else refreshGroups();
    if (failed.length) toast.error(t('categories.list.failed', { names: failed.join(', ') }));
    else toast.success(t('categories.list.deleted', { count: toDelete.ids.length }));
  };

  // Activation en lot des groupes.
  const [bulkBusy, setBulkBusy] = useState(false);
  const setActiveBulk = async (targets: CategoryGroup[], active: boolean) => {
    setBulkBusy(true);
    const failed: string[] = [];
    for (const g of targets) {
      try { await apiService.put(`/category-groups/${g.id}`, { is_active: active }); } catch { failed.push(g.name); }
    }
    setBulkBusy(false); setSelection({}); refreshGroups();
    if (failed.length) toast.error(t('categories.list.failed', { names: failed.join(', ') }));
    else toast.success(t(active ? 'categoryGroups.list.activated' : 'categoryGroups.list.deactivated', { count: targets.length }));
  };

  // ----- Mode réorganisation -----
  // Chaque dépôt écrit l'ordre entier tout de suite ; « Annuler » sur le
  // message rejoue l'ordre d'avant (une écriture de plus, jamais un état gardé
  // de côté). Un refus remet la liste comme elle était.
  const [ordering, setOrdering] = useState<Category[] | null>(null);
  const writeOrder = async (items: Category[], previous: Category[], withUndo: boolean) => {
    if (!selectedEventId) return;
    setOrdering(items);
    try {
      await apiService.updateCategoriesOrder(selectedEventId, items.map((c) => c.id));
      refreshCategories();
      if (withUndo) {
        toast.success(t('categories.list.orderChanged'), {
          action: { label: t('common.undo'), onClick: () => { void writeOrder(previous, items, false); } },
        });
      }
    } catch (err) {
      setOrdering(previous);
      toast.error(formatError(err, t('errors.generic')));
    }
  };
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !ordering || result.destination.index === result.source.index) return;
    const items = Array.from(ordering);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    void writeOrder(items, ordering, true);
  };

  // ----- Colonnes -----
  const categoryColumns = useMemo<ColumnDef<Category, any>[]>(() => [
    {
      id: 'order', accessorFn: (c) => c.display_order ?? 999, header: () => '#',
      cell: ({ row }) => <span className="tabular-nums text-muted-foreground">{row.original.display_order ?? '—'}</span>,
    },
    {
      id: 'name', accessorFn: (c) => c.name, header: () => t('categories.categoryName'),
      sortingFn: (a, b) => a.original.name.localeCompare(b.original.name, undefined, { sensitivity: 'base' }),
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-semibold text-foreground">{row.original.name}</div>
          {row.original.display_name && <div className="mt-0.5 text-xs text-muted-foreground">{row.original.display_name}</div>}
        </div>
      ),
    },
    { id: 'gender', accessorFn: (c) => genderLabel(c.gender), header: () => t('categories.gender'), cell: ({ getValue }) => <span className="text-muted-foreground">{getValue()}</span> },
    { id: 'age', accessorFn: (c) => c.min_age ?? 0, header: () => t('categories.age'), cell: ({ row }) => <span className="whitespace-nowrap text-muted-foreground">{ageLabel(row.original)}</span> },
    {
      id: 'teams', accessorFn: (c) => teamCount(c.id), header: () => t('categories.list.colTeams'),
      cell: ({ getValue }) => <span className="tabular-nums">{teamsQuery.isLoading ? '…' : getValue()}</span>,
    },
    {
      id: 'description', accessorFn: (c) => c.description ?? '', header: () => t('common.description'), enableSorting: false,
      cell: ({ getValue }) => <span className="line-clamp-1 max-w-[360px] text-muted-foreground">{getValue() || '—'}</span>,
    },
    {
      id: 'actions', enableSorting: false, header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={t('categories.list.rowActions', { name: row.original.name })}><MoreHorizontal size={16} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => openRow(row.original.id)}><Pencil size={16} className="mr-2" />{t('common.edit')}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setToDelete({ ids: [row.original.id], names: [row.original.name] })}><Trash2 size={16} className="mr-2" />{t('common.delete')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, genderLabel, ageLabel, teamCount, teamsQuery.isLoading]);

  const groupColumns = useMemo<ColumnDef<CategoryGroup, any>[]>(() => [
    {
      id: 'name', accessorFn: (g) => g.name, header: () => t('categoryGroups.table.groupName'),
      sortingFn: (a, b) => a.original.name.localeCompare(b.original.name, undefined, { sensitivity: 'base' }),
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-semibold text-foreground">{row.original.name}</div>
          {row.original.description && <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{row.original.description}</div>}
        </div>
      ),
    },
    {
      id: 'categories', accessorFn: (g) => g.category_ids.length, header: () => t('categoryGroups.table.categories'),
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.category_ids.map(categoryName).join(' · ')}</span>,
    },
    {
      id: 'status', accessorFn: (g) => (g.is_active ? 1 : 0), header: () => t('categoryGroups.table.status'),
      cell: ({ row }) => <StatusDot tone={row.original.is_active ? 'success' : 'neutral'} label={row.original.is_active ? t('categoryGroups.statusActive') : t('categoryGroups.statusInactive')} className="text-muted-foreground" />,
    },
    {
      id: 'actions', enableSorting: false, header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={t('categories.list.rowActions', { name: row.original.name })}><MoreHorizontal size={16} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => openRow(row.original.id)}><Pencil size={16} className="mr-2" />{t('common.edit')}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveBulk([row.original], !row.original.is_active)}>{row.original.is_active ? t('categoryGroups.list.deactivate') : t('categoryGroups.list.activate')}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setToDelete({ ids: [row.original.id], names: [row.original.name] })}><Trash2 size={16} className="mr-2" />{t('common.delete')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, categoryName]);

  if (!selectedEventId) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader title={t('categories.title')} />
        <p className="mt-8 text-muted-foreground">{t('categories.selectEventToManage')}</p>
      </div>
    );
  }

  const selectedGroups = groups.filter((g) => selection[g.id]);
  const isError = tab === 'categories' ? categoriesQuery.isError : groupsQuery.isError;
  const refetch = () => (tab === 'categories' ? categoriesQuery.refetch() : groupsQuery.refetch());

  return (
    <div className={cn('space-y-5 p-4 transition-[padding] duration-200 md:p-8', sheetOpen && 'min-[1600px]:pr-[544px]')}>
      <PageHeader
        eyebrow={selectedEvent?.name}
        title={t('categories.title')}
        meta={[t('categories.count', { count: categories.length }), groups.length ? t('categoryGroups.groupsCount', { count: groups.length }) : null].filter(Boolean).join(' · ')}
        actions={tab === 'categories' ? (
          ordering ? (
            <Button size="sm" onClick={() => setOrdering(null)}><Check size={16} />{t('common.done')}</Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => { closeSheet(); setOrdering([...categories]); }} disabled={categories.length < 2}><ArrowUpDown size={16} />{t('common.reorder')}</Button>
              <Button size="sm" onClick={() => { setSheetId(null); setSheetCreate(true); }}><Plus size={16} />{t('categories.newCategory')}</Button>
            </>
          )
        ) : (
          <Button size="sm" onClick={() => { setSheetId(null); setSheetCreate(true); }}><Plus size={16} />{t('categoryGroups.createGroup')}</Button>
        )}
      />

      <UnderlineTabs
        value={tab}
        onChange={(v) => { if (!ordering) setTab(v); }}
        items={[{ value: 'categories', label: t('categories.title') }, { value: 'groups', label: t('categoryGroups.tabLabel') }]}
        aria-label={t('categories.title')}
      />

      {tab === 'groups' && <p className="max-w-3xl text-sm text-muted-foreground">{t('categoryGroups.infoMessage')}</p>}

      {ordering ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{t('categories.orderingModeHint')}</p>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="categories-order">
              {(provided) => (
                <ul ref={provided.innerRef} {...provided.droppableProps} className="divide-y divide-white/[0.06] border-y border-white/[0.08]">
                  {ordering.map((c, index) => (
                    <Draggable key={c.id} draggableId={c.id} index={index}>
                      {(p, snapshot) => (
                        <li ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}
                          className={cn('flex cursor-grab items-center gap-3 px-3 py-3 text-sm active:cursor-grabbing', snapshot.isDragging && 'bg-white/[0.07]')}>
                          <GripVertical size={16} className="text-muted-foreground" />
                          <span className="w-6 tabular-nums text-muted-foreground">{index + 1}</span>
                          <span className="font-semibold">{c.name}</span>
                          <span className="ml-auto text-xs text-muted-foreground">{t('categories.list.teamsShort', { count: teamCount(c.id) })}</span>
                        </li>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      ) : (
        <>
          <Toolbar>
            <SearchField value={query} onChange={setQuery} placeholder={tab === 'categories' ? t('categories.list.searchPlaceholder') : t('categoryGroups.list.searchPlaceholder')} />
            {tab === 'categories' ? (
              <FilterChip
                label={t('categories.gender')}
                selected={genderFilter}
                onChange={setGenderFilter}
                options={['M', 'F', 'MIXED', 'none'].map((g) => ({ value: g, label: genderLabel(g === 'none' ? undefined : g), count: categories.filter((c) => (c.gender ?? 'none') === g).length })).filter((o) => o.count > 0)}
              />
            ) : (
              <FilterChip
                label={t('categoryGroups.table.status')}
                selected={activeFilter}
                onChange={setActiveFilter}
                options={[
                  { value: 'active', label: t('categoryGroups.statusActive'), count: groups.filter((g) => g.is_active).length },
                  { value: 'inactive', label: t('categoryGroups.statusInactive'), count: groups.filter((g) => !g.is_active).length },
                ].filter((o) => o.count > 0)}
              />
            )}
            {(query || genderFilter.length || activeFilter.length) ? (
              <span className="text-xs text-muted-foreground">
                {t('teams.filteredCount', { shown: tab === 'categories' ? filteredCategories.length : filteredGroups.length, total: tab === 'categories' ? categories.length : groups.length })}
              </span>
            ) : null}
          </Toolbar>

          {isError && (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-4">
                {t('errors.generic')}
                <Button variant="outline" size="sm" onClick={refetch}>{t('common.retry')}</Button>
              </AlertDescription>
            </Alert>
          )}

          {tab === 'categories' ? (
            <DataTable
              ariaLabel={t('categories.title')}
              columns={categoryColumns}
              data={filteredCategories}
              getRowId={(c) => c.id}
              loading={categoriesQuery.isLoading}
              selectable
              rowSelection={selection}
              onRowSelectionChange={setSelection}
              onRowClick={(c) => openRow(c.id)}
              activeRowId={sheetCategory?.id ?? null}
              initialSorting={[{ id: 'order', desc: false }]}
              empty={categories.length === 0 ? (
                <div className="space-y-4">
                  <p className="text-base text-foreground">{t('categories.noCategories')}</p>
                  <p>{t('categories.noCategoriesHint')}</p>
                  <Button onClick={() => setSheetCreate(true)}><Plus size={16} />{t('categories.newCategory')}</Button>
                </div>
              ) : t('categories.list.noMatch')}
              renderCard={(c, { selected, toggle }) => (
                <div className="flex items-start gap-3 py-3">
                  <input type="checkbox" checked={selected} onChange={toggle} onClick={(e) => e.stopPropagation()} aria-label={t('dataTable.selectRow')} className="mt-1 h-4 w-4 accent-white" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{c.name}</div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{genderLabel(c.gender)}</span><span>{ageLabel(c)}</span><span>{t('categories.list.teamsShort', { count: teamCount(c.id) })}</span>
                    </div>
                  </div>
                </div>
              )}
            />
          ) : (
            <DataTable
              ariaLabel={t('categoryGroups.title')}
              columns={groupColumns}
              data={filteredGroups}
              getRowId={(g) => g.id}
              loading={groupsQuery.isLoading}
              selectable
              rowSelection={selection}
              onRowSelectionChange={setSelection}
              onRowClick={(g) => openRow(g.id)}
              activeRowId={sheetGroup?.id ?? null}
              initialSorting={[{ id: 'name', desc: false }]}
              empty={groups.length === 0 ? (
                <div className="space-y-4">
                  <p>{t('categoryGroups.noGroups')}</p>
                  <Button onClick={() => setSheetCreate(true)} disabled={categories.length < 2}><Plus size={16} />{t('categoryGroups.createGroup')}</Button>
                </div>
              ) : t('categories.list.noMatch')}
              renderCard={(g, { selected, toggle }) => (
                <div className="flex items-start gap-3 py-3">
                  <input type="checkbox" checked={selected} onChange={toggle} onClick={(e) => e.stopPropagation()} aria-label={t('dataTable.selectRow')} className="mt-1 h-4 w-4 accent-white" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{g.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{g.category_ids.map(categoryName).join(' · ')}</div>
                    <div className="mt-1 text-xs"><StatusDot tone={g.is_active ? 'success' : 'neutral'} label={g.is_active ? t('categoryGroups.statusActive') : t('categoryGroups.statusInactive')} className="text-muted-foreground" /></div>
                  </div>
                </div>
              )}
            />
          )}
        </>
      )}

      <BulkActionBar count={selectedIds.length} onClear={() => setSelection({})}>
        {tab === 'groups' && (
          <>
            <BulkAction disabled={bulkBusy} onClick={() => setActiveBulk(selectedGroups, true)}>{t('categoryGroups.list.activate')}</BulkAction>
            <BulkAction disabled={bulkBusy} onClick={() => setActiveBulk(selectedGroups, false)}>{t('categoryGroups.list.deactivate')}</BulkAction>
          </>
        )}
        <BulkAction className="text-destructive" onClick={() => setToDelete({
          ids: selectedIds,
          names: selectedIds.map((id) => (tab === 'categories' ? categoryName(id) : groups.find((g) => g.id === id)?.name ?? id)),
        })}><Trash2 />{t('common.delete')}</BulkAction>
      </BulkActionBar>

      <CategorySheet
        open={tab === 'categories' && sheetOpen}
        category={sheetCreate ? null : sheetCategory}
        teamCount={sheetCategory ? teamCount(sheetCategory.id) : undefined}
        saving={saving}
        serverError={serverError}
        onSubmit={submitCategory}
        onPatch={patchCategory}
        onRemove={() => { if (sheetCategory) setToDelete({ ids: [sheetCategory.id], names: [sheetCategory.name] }); }}
        onClose={closeSheet}
        onDirtyChange={setSheetDirty}
        onPrev={indexInView > 0 ? () => setSheetId(visibleIds[indexInView - 1]) : undefined}
        onNext={indexInView >= 0 && indexInView < visibleIds.length - 1 ? () => setSheetId(visibleIds[indexInView + 1]) : undefined}
      />
      <CategoryGroupSheet
        open={tab === 'groups' && sheetOpen}
        group={sheetCreate ? null : sheetGroup}
        categories={categories}
        saving={saving}
        serverError={serverError}
        onSubmit={submitGroup}
        onPatch={patchGroup}
        onRemove={() => { if (sheetGroup) setToDelete({ ids: [sheetGroup.id], names: [sheetGroup.name] }); }}
        onClose={closeSheet}
        onDirtyChange={setSheetDirty}
        onPrev={indexInView > 0 ? () => setSheetId(visibleIds[indexInView - 1]) : undefined}
        onNext={indexInView >= 0 && indexInView < visibleIds.length - 1 ? () => setSheetId(visibleIds[indexInView + 1]) : undefined}
      />

      <ConfirmDialog
        open={pendingSwitch !== null}
        onOpenChange={(o) => { if (!o) setPendingSwitch(null); }}
        title={t('teams.form.discardTitle')}
        message={t('teams.form.discardMessage')}
        confirmLabel={t('teams.form.discardConfirm')}
        destructive
        onConfirm={() => { setSheetDirty(false); setSheetCreate(false); setSheetId(pendingSwitch); setPendingSwitch(null); }}
      />
      <ConfirmDialog
        open={toDelete.ids.length > 0}
        onOpenChange={(o) => { if (!o && !deleting) setToDelete({ ids: [], names: [] }); }}
        title={t('common.confirmDeleteTitle')}
        message={toDelete.ids.length === 1
          ? (tab === 'categories' ? t('categories.deleteConfirm', { name: toDelete.names[0] }) : t('categoryGroups.deleteConfirm'))
          : t('categories.list.deleteManyConfirm', { count: toDelete.ids.length })}
        confirmLabel={t('common.delete')}
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default Categories;
